import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, Pencil, RotateCcw, ChevronDown, ChevronRight, FlaskConical, X, Trash2 } from "lucide-react";

const emptyMarker = () => ({
  category: "", name: "", unit: "",
  ref_min: "", ref_max: "",
  ref_min_h: "", ref_max_h: "", ref_min_m: "", ref_max_m: "",
  ideal_min: "", ideal_max: "",
  ideal_min_h: "", ideal_max_h: "", ideal_min_m: "", ideal_max_m: "",
  gender_differentiated: false,
  observation: "",
  use_custom_ranges: false,
  direction: "",
  custom_ranges: [],
});

const emptyCustomRange = () => ({ min: "", max: "", label: "", level: "bom" });
const LEVEL_OPTIONS = [
  { value: "ruim", label: "Ruim" },
  { value: "regular", label: "Regular" },
  { value: "bom", label: "Bom" },
  { value: "otimo", label: "Ótimo" },
  { value: "excelente", label: "Excelente" },
];
const rangeText = (r) => {
  const mn = r.min === "" || r.min === null || r.min === undefined ? null : Number(r.min);
  const mx = r.max === "" || r.max === null || r.max === undefined ? null : Number(r.max);
  if (mn === null && mx === null) return "sem limites";
  if (mn === null) return `< ${mx}`;
  if (mx === null) return `≥ ${mn}`;
  return `${mn} … < ${mx}`;
};

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const fmtNum = (v) => (v === null || v === undefined || v === "" ? "—" : new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 3 }).format(v));

function RangeText({ min, max, unit }) {
  if (min === null && max === null) return "—";
  return `${fmtNum(min)}–${fmtNum(max)}${unit ? ` ${unit}` : ""}`;
}

function OverrideModal({ marker, onClose, onSaved }) {
  const [form, setForm] = useState({
    ideal_min: marker.effective_ideal_min ?? marker.ideal_min ?? "",
    ideal_max: marker.effective_ideal_max ?? marker.ideal_max ?? "",
    ideal_min_h: marker.effective_ideal_min_h ?? marker.ideal_min_h ?? "",
    ideal_max_h: marker.effective_ideal_max_h ?? marker.ideal_max_h ?? "",
    ideal_min_m: marker.effective_ideal_min_m ?? marker.ideal_min_m ?? "",
    ideal_max_m: marker.effective_ideal_max_m ?? marker.ideal_max_m ?? "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      const fields = marker.gender_differentiated
        ? ["ideal_min_h", "ideal_max_h", "ideal_min_m", "ideal_max_m"]
        : ["ideal_min", "ideal_max"];
      for (const f of fields) {
        if (form[f] !== "" && form[f] !== null) payload[f] = Number(form[f]);
      }
      if (Object.keys(payload).length === 0) { toast.error("Informe pelo menos um valor."); setSaving(false); return; }
      await api.put(`/admin/markers/${marker.id}/override`, payload);
      toast.success("Valor ideal personalizado salvo.");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar.");
    }
    setSaving(false);
  };

  const restore = async () => {
    if (!marker.is_customized) { onClose(); return; }
    if (!window.confirm("Restaurar o valor padrão? Sua personalização será removida.")) return;
    setSaving(true);
    try {
      await api.delete(`/admin/markers/${marker.id}/override`);
      toast.success("Personalização removida.");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao restaurar.");
    }
    setSaving(false);
  };

  const fld = (label, k) => (
    <div>
      <label className="overline text-muted-foreground mb-1 block">{label}</label>
      <input
        type="number" step="0.01" value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
        data-testid={`override-${k}`}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={onClose} data-testid="override-modal">
      <div className="bg-card border border-border rounded-sm p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline text-gold mb-1">Editar valor ideal</div>
            <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">{marker.name}</h3>
            <div className="text-xs text-muted-foreground mt-1">{marker.category} · Padrão: {marker.gender_differentiated
              ? `H ${fmtNum(marker.ideal_min_h)}–${fmtNum(marker.ideal_max_h)} · M ${fmtNum(marker.ideal_min_m)}–${fmtNum(marker.ideal_max_m)}`
              : <RangeText min={marker.ideal_min} max={marker.ideal_max} unit={marker.unit} />}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm"><X className="w-4 h-4" /></button>
        </div>

        {marker.gender_differentiated ? (
          <div className="space-y-4">
            <div>
              <div className="overline text-gold mb-2">Homens</div>
              <div className="grid grid-cols-2 gap-3">
                {fld("Ideal mín.", "ideal_min_h")}
                {fld("Ideal máx.", "ideal_max_h")}
              </div>
            </div>
            <div>
              <div className="overline text-gold mb-2">Mulheres</div>
              <div className="grid grid-cols-2 gap-3">
                {fld("Ideal mín.", "ideal_min_m")}
                {fld("Ideal máx.", "ideal_max_m")}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {fld("Ideal mín.", "ideal_min")}
            {fld("Ideal máx.", "ideal_max")}
          </div>
        )}

        <div className="flex gap-2 justify-between pt-6">
          <button
            onClick={restore} disabled={saving || !marker.is_customized}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-sm border border-border text-xs font-semibold hover:bg-muted disabled:opacity-40 transition-colors"
            data-testid="restore-default-btn"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Restaurar padrão
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted">Cancelar</button>
            <button onClick={save} disabled={saving} className="px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60" data-testid="save-override-btn">
              {saving ? "Salvando…" : "Salvar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomRangesEditor({ ranges, onChange, direction, onDirectionChange }) {
  const addRow = () => onChange([...(ranges || []), emptyCustomRange()]);
  const updateRow = (idx, patch) =>
    onChange(ranges.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeRow = (idx) => onChange(ranges.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3" data-testid="custom-ranges-editor">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
        <div>
          <label className="overline text-muted-foreground mb-1 block">Direção</label>
          <select
            value={direction || ""}
            onChange={(e) => onDirectionChange(e.target.value)}
            className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
            data-testid="marker-direction-select"
          >
            <option value="">Neutra / não se aplica</option>
            <option value="higher_is_better">Quanto MAIOR, melhor (ex: Elastase)</option>
            <option value="lower_is_better">Quanto MENOR, melhor (ex: Calprotectina)</option>
          </select>
        </div>
        <p className="text-xs text-muted-foreground">
          Convenção: limite inferior <b>inclusivo</b>, limite superior <b>exclusivo</b>. Ex.:
          faixa <code>50 – 200</code> vale para <code>50 ≤ valor &lt; 200</code>.
        </p>
      </div>

      <div className="rounded-sm border border-border overflow-hidden">
        <div className="hidden md:grid grid-cols-[100px_100px_1fr_150px_36px] gap-2 px-3 py-2 bg-muted/40 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
          <div>Valor mín.</div>
          <div>Valor máx.</div>
          <div>Rótulo</div>
          <div>Nível</div>
          <div />
        </div>
        <div className="divide-y divide-border">
          {(ranges || []).map((r, idx) => (
            <div
              key={idx}
              className="grid grid-cols-2 md:grid-cols-[100px_100px_1fr_150px_36px] gap-2 px-3 py-2 items-center"
              data-testid={`custom-range-row-${idx}`}
            >
              <input
                type="number" step="0.01"
                placeholder="—"
                value={r.min}
                onChange={(e) => updateRow(idx, { min: e.target.value })}
                className="w-full px-2 py-1.5 rounded-sm border border-input bg-background text-sm"
                data-testid={`custom-range-min-${idx}`}
              />
              <input
                type="number" step="0.01"
                placeholder="—"
                value={r.max}
                onChange={(e) => updateRow(idx, { max: e.target.value })}
                className="w-full px-2 py-1.5 rounded-sm border border-input bg-background text-sm"
                data-testid={`custom-range-max-${idx}`}
              />
              <input
                type="text"
                placeholder="ex: Positivo baixo, Insuficiência grave…"
                value={r.label}
                onChange={(e) => updateRow(idx, { label: e.target.value })}
                className="w-full col-span-2 md:col-span-1 px-2 py-1.5 rounded-sm border border-input bg-background text-sm"
                data-testid={`custom-range-label-${idx}`}
              />
              <select
                value={r.level}
                onChange={(e) => updateRow(idx, { level: e.target.value })}
                className="w-full px-2 py-1.5 rounded-sm border border-input bg-background text-sm"
                data-testid={`custom-range-level-${idx}`}
              >
                {LEVEL_OPTIONS.map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => removeRow(idx)}
                className="p-1.5 rounded-sm border border-destructive/30 text-destructive hover:bg-destructive/5 transition-colors justify-self-end"
                data-testid={`custom-range-remove-${idx}`}
                aria-label="Remover faixa"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              <div className="col-span-2 md:col-span-5 -mt-1 pl-1 text-[10px] text-muted-foreground">
                Faixa: <span className="font-medium">{rangeText(r)}</span>
              </div>
            </div>
          ))}
          {(ranges || []).length === 0 && (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              Nenhuma faixa cadastrada.
            </div>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={addRow}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-primary/40 text-primary text-sm hover:bg-primary/5 transition-colors"
        data-testid="custom-range-add-btn"
      >
        <Plus className="w-3.5 h-3.5" /> Adicionar faixa
      </button>
    </div>
  );
}

function CreateMarkerModal({ categories, onClose, onSaved }) {
  const [form, setForm] = useState(emptyMarker());
  const [customCategory, setCustomCategory] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.category.trim() || !form.name.trim()) { toast.error("Informe categoria e nome."); return; }
    if (form.use_custom_ranges) {
      const cleaned = (form.custom_ranges || []).filter((r) => {
        const hasBound = r.min !== "" || r.max !== "";
        return r.label && r.label.trim() && r.level && hasBound;
      });
      if (cleaned.length === 0) {
        toast.error("Adicione ao menos uma faixa customizada válida (rótulo + limite).");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        category: form.category.trim(), name: form.name.trim(), unit: form.unit,
        gender_differentiated: !!form.gender_differentiated,
        observation: form.observation,
        use_custom_ranges: !!form.use_custom_ranges,
        direction: form.direction || null,
      };
      const rangeFields = ["ref_min", "ref_max", "ref_min_h", "ref_max_h", "ref_min_m", "ref_max_m",
                          "ideal_min", "ideal_max", "ideal_min_h", "ideal_max_h", "ideal_min_m", "ideal_max_m"];
      for (const f of rangeFields) {
        const n = num(form[f]);
        if (n !== null) payload[f] = n;
      }
      if (form.use_custom_ranges) {
        payload.custom_ranges = (form.custom_ranges || []).map((r) => ({
          min: num(r.min),
          max: num(r.max),
          label: (r.label || "").trim(),
          level: r.level,
        }));
      }
      await api.post("/admin/markers", payload);
      toast.success("Marcador criado.");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar."); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={onClose} data-testid="create-marker-modal">
      <div className="bg-card border border-border rounded-sm p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline text-gold mb-1">Novo marcador</div>
            <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">Adicionar ao catálogo</h3>
            <p className="text-xs text-muted-foreground mt-1">Marcadores criados por você ficam visíveis apenas no seu perfil.</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm"><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="overline text-muted-foreground mb-1 block">Categoria</label>
              {customCategory ? (
                <div className="flex gap-2">
                  <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="flex-1 px-3 py-2 rounded-sm border border-input bg-card text-sm"
                    placeholder="Ex: Perfil Hormonal" data-testid="new-category-input" />
                  <button type="button" onClick={() => setCustomCategory(false)} className="text-xs text-muted-foreground hover:text-primary">Selecionar</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="flex-1 px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="category-select">
                    <option value="">Selecione…</option>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <button type="button" onClick={() => setCustomCategory(true)} className="text-xs text-primary hover:underline" data-testid="new-category-btn">+ Nova</button>
                </div>
              )}
            </div>
            <div>
              <label className="overline text-muted-foreground mb-1 block">Nome</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="marker-name-input" />
            </div>
          </div>
          <div>
            <label className="overline text-muted-foreground mb-1 block">Unidade</label>
            <input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
              className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
              placeholder="ex: mg/dL, ng/mL" data-testid="marker-unit-input" />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.gender_differentiated}
              onChange={(e) => setForm({ ...form, gender_differentiated: e.target.checked })}
              className="accent-primary w-4 h-4" data-testid="gender-diff-checkbox" />
            <span>Valores diferenciados por sexo (H/M)</span>
          </label>

          {!form.use_custom_ranges && (form.gender_differentiated ? (
            <>
              <div>
                <div className="overline text-gold mb-2">Homens</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <NumInput label="Ref. mín" val={form.ref_min_h} onChange={(v) => setForm({ ...form, ref_min_h: v })} />
                  <NumInput label="Ref. máx" val={form.ref_max_h} onChange={(v) => setForm({ ...form, ref_max_h: v })} />
                  <NumInput label="Ideal mín" val={form.ideal_min_h} onChange={(v) => setForm({ ...form, ideal_min_h: v })} />
                  <NumInput label="Ideal máx" val={form.ideal_max_h} onChange={(v) => setForm({ ...form, ideal_max_h: v })} />
                </div>
              </div>
              <div>
                <div className="overline text-gold mb-2">Mulheres</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <NumInput label="Ref. mín" val={form.ref_min_m} onChange={(v) => setForm({ ...form, ref_min_m: v })} />
                  <NumInput label="Ref. máx" val={form.ref_max_m} onChange={(v) => setForm({ ...form, ref_max_m: v })} />
                  <NumInput label="Ideal mín" val={form.ideal_min_m} onChange={(v) => setForm({ ...form, ideal_min_m: v })} />
                  <NumInput label="Ideal máx" val={form.ideal_max_m} onChange={(v) => setForm({ ...form, ideal_max_m: v })} />
                </div>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <NumInput label="Ref. mín" val={form.ref_min} onChange={(v) => setForm({ ...form, ref_min: v })} />
              <NumInput label="Ref. máx" val={form.ref_max} onChange={(v) => setForm({ ...form, ref_max: v })} />
              <NumInput label="Ideal mín" val={form.ideal_min} onChange={(v) => setForm({ ...form, ideal_min: v })} />
              <NumInput label="Ideal máx" val={form.ideal_max} onChange={(v) => setForm({ ...form, ideal_max: v })} />
            </div>
          ))}

          <div className="rounded-sm border border-primary/30 bg-primary/5 p-3">
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.use_custom_ranges}
                onChange={(e) => setForm({
                  ...form,
                  use_custom_ranges: e.target.checked,
                  custom_ranges: e.target.checked && (form.custom_ranges || []).length === 0
                    ? [emptyCustomRange(), emptyCustomRange()]
                    : form.custom_ranges,
                })}
                className="accent-primary w-4 h-4 mt-0.5"
                data-testid="use-custom-ranges-checkbox"
              />
              <span>
                <b>Usar faixas customizadas</b>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  Substitui a lógica padrão Ref./Ideal. Cada faixa recebe um rótulo próprio (ex: "Positivo baixo") e um nível associado ao selo colorido.
                </span>
              </span>
            </label>
            {form.use_custom_ranges && (
              <div className="mt-4">
                <CustomRangesEditor
                  ranges={form.custom_ranges}
                  onChange={(rs) => setForm({ ...form, custom_ranges: rs })}
                  direction={form.direction}
                  onDirectionChange={(d) => setForm({ ...form, direction: d })}
                />
              </div>
            )}
          </div>

          <div>
            <label className="overline text-muted-foreground mb-1 block">Observação</label>
            <textarea rows={2} value={form.observation} onChange={(e) => setForm({ ...form, observation: e.target.value })}
              className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
              placeholder="Ex: Quanto menor, melhor. Coletar em jejum."
              data-testid="marker-observation-input" />
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <button onClick={onClose} className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted">Cancelar</button>
            <button onClick={save} disabled={saving} className="px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60" data-testid="save-new-marker-btn">
              {saving ? "Criando…" : "Criar marcador"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function NumInput({ label, val, onChange }) {
  return (
    <div>
      <label className="overline text-muted-foreground mb-1 block">{label}</label>
      <input type="number" step="0.01" value={val} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" />
    </div>
  );
}

function MarkerRow({ m, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const canExpand = !!m.observation;
  const ref = m.gender_differentiated
    ? `H: ${fmtNum(m.ref_min_h)}–${fmtNum(m.ref_max_h)} / M: ${fmtNum(m.ref_min_m)}–${fmtNum(m.ref_max_m)}`
    : `${fmtNum(m.ref_min)}–${fmtNum(m.ref_max)}`;
  const ideal = m.gender_differentiated
    ? `H: ${fmtNum(m.effective_ideal_min_h)}–${fmtNum(m.effective_ideal_max_h)} / M: ${fmtNum(m.effective_ideal_min_m)}–${fmtNum(m.effective_ideal_max_m)}`
    : `${fmtNum(m.effective_ideal_min)}–${fmtNum(m.effective_ideal_max)}`;

  return (
    <div className="border-b border-border last:border-0" data-testid={`marker-row-${m.id}`}>
      <div className="flex items-start gap-3 px-4 py-3 hover:bg-muted/40 transition-colors">
        {canExpand ? (
          <button onClick={() => setExpanded((v) => !v)} className="p-0.5 mt-0.5 hover:bg-muted rounded-sm flex-none" data-testid={`toggle-obs-${m.id}`}>
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        ) : (
          <div className="w-5 flex-none" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-heading font-semibold text-sm">{m.name}</span>
            {m.is_customized && (
              <span className="inline-flex overline px-1.5 py-0.5 rounded-sm border border-gold/40 text-gold bg-gold/5" data-testid={`badge-customized-${m.id}`}>
                Personalizado
              </span>
            )}
            {m.gender_differentiated && (
              <span className="inline-flex overline px-1.5 py-0.5 rounded-sm border border-primary/30 text-primary bg-primary/5">
                diferenciado
              </span>
            )}
            {m.is_custom_marker && (
              <span className="inline-flex overline px-1.5 py-0.5 rounded-sm border border-accent/40 text-accent bg-accent/5">
                meu
              </span>
            )}
            {m.use_custom_ranges && Array.isArray(m.custom_ranges) && m.custom_ranges.length > 0 && (
              <span
                className="inline-flex overline px-1.5 py-0.5 rounded-sm border border-primary/40 text-primary bg-primary/5"
                title="Marcador com faixas customizadas"
                data-testid={`badge-custom-ranges-${m.id}`}
              >
                {m.custom_ranges.length} faixas
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {m.use_custom_ranges && Array.isArray(m.custom_ranges) && m.custom_ranges.length > 0 ? (
              <>
                {m.unit ? `${m.unit} · ` : ""}Faixas:{" "}
                {m.custom_ranges
                  .slice()
                  .sort((a, b) => (a.min ?? -Infinity) - (b.min ?? -Infinity))
                  .map((r, i) => (
                    <span key={i} className="inline-block mr-2">
                      <span className="font-medium">{r.label}</span>{" "}
                      <span className="text-muted-foreground/80">
                        ({r.min == null ? "<" : "≥ "}{r.min ?? r.max}{r.min == null || r.max == null ? "" : ` … < ${r.max}`}{r.min != null && r.max == null ? "" : ""})
                      </span>
                    </span>
                  ))}
              </>
            ) : (
              <>{m.unit ? `${m.unit} · ` : ""}Ref: {ref} · Ideal: <span className={m.is_customized ? "text-gold font-semibold" : ""}>{ideal}</span></>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-none">
          <button
            onClick={() => onEdit(m)}
            className="p-1.5 rounded-sm border border-border hover:bg-muted transition-colors"
            title="Editar valor ideal"
            data-testid={`edit-marker-${m.id}`}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          {m.is_custom_marker && (
            <button
              onClick={() => onDelete(m)}
              className="p-1.5 rounded-sm border border-destructive/30 text-destructive hover:bg-destructive/5 transition-colors"
              title="Excluir marcador"
              data-testid={`delete-marker-${m.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      {expanded && m.observation && (
        <div className="pl-14 pr-4 pb-3 text-xs text-muted-foreground italic" data-testid={`obs-${m.id}`}>
          {m.observation}
        </div>
      )}
    </div>
  );
}

export default function AdminMarkersPage() {
  const [markers, setMarkers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/markers");
      setMarkers(data || []);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao carregar dados."); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return markers;
    return markers.filter((m) => m.name.toLowerCase().includes(q) || m.category.toLowerCase().includes(q));
  }, [markers, search]);

  const grouped = useMemo(() => {
    const g = {};
    for (const m of filtered) {
      (g[m.category] = g[m.category] || []).push(m);
    }
    return g;
  }, [filtered]);

  const categories = useMemo(() => [...new Set(markers.map((m) => m.category))].sort(), [markers]);

  const removeCustom = async (m) => {
    if (!window.confirm(`Excluir o marcador "${m.name}"? Esta ação é permanente.`)) return;
    try {
      await api.delete(`/admin/markers/${m.id}`);
      toast.success("Marcador excluído.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao excluir."); }
  };

  return (
    <div className="space-y-6" data-testid="admin-markers-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-gold mb-3 inline-flex items-center gap-2"><FlaskConical className="w-3.5 h-3.5" /> Biblioteca</div>
          <h1 className="brand-wordmark text-3xl md:text-4xl font-bold tracking-tight uppercase text-primary">Biblioteca de Marcadores</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Faixas de referência tradicionais e ideais funcionais. Personalize os valores — eles ficam salvos no seu perfil profissional.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
          data-testid="new-marker-btn"
        >
          <Plus className="w-4 h-4" /> Criar Marcador
        </button>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar marcador por nome ou categoria…"
          className="w-full pl-10 pr-3 py-2.5 rounded-sm border border-input bg-card text-sm"
          data-testid="markers-search"
        />
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-muted-foreground overline">Carregando…</div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">Nenhum marcador encontrado.</div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} className="bg-card border border-border rounded-sm overflow-hidden" data-testid={`marker-group-${cat.replace(/\s/g, "-")}`}>
              <div className="px-4 py-2.5 bg-muted/50 border-b border-border">
                <div className="overline text-gold tracking-[0.22em]">{cat}</div>
                <div className="text-[10px] text-muted-foreground">{items.length} marcador{items.length !== 1 ? "es" : ""}</div>
              </div>
              <div>
                {items.map((m) => (
                  <MarkerRow key={m.id} m={m} onEdit={setEditing} onDelete={removeCustom} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <OverrideModal marker={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />}
      {creating && <CreateMarkerModal categories={categories} onClose={() => setCreating(false)} onSaved={async () => { setCreating(false); await load(); }} />}
    </div>
  );
}
